import { api, getData, postData, type Envelope } from "@/services/api";

export interface MeUser {
  id: string;
  org_id: string;
  org_slug: string;
  org_name: string;
  email: string;
  name: string;
  locale: string;
  totp_enabled: boolean;
  status: string;
  presence_status?: string;
  /** Última escolha MANUAL (available/away) — o estado que a reconexão restaura.
   *  O alerta de "ficar disponível" no login decide por AQUI, não por
   *  presence_status (transitório: o logout força unavailable nos dois casos). */
  last_manual_presence?: string;
  /** F1 (0146): flag da org — oculta o filtro "Todas" do inbox p/ quem não tem read_all. */
  agents_see_only_own_conversations?: boolean;
}

export interface LoginResponse {
  access_token: string;
  access_expires_at: string;
  token_type: string;
  user: MeUser;
  permissions: string[];
}

export interface MeBundle {
  user: MeUser;
  permissions: string[];
}

export interface SessionDTO {
  id: string;
  user_agent: string;
  ip_address: string;
  issued_at: string;
  last_used_at: string;
  expires_at: string;
  is_current: boolean;
  prefix: string;
}

export interface TOTPStart {
  secret: string;
  otpauth_url: string;
  qr_data_url: string;
}

export const authService = {
  async login(email: string, password: string, opts: { orgSlug?: string; totp?: string } = {}): Promise<LoginResponse> {
    return postData<LoginResponse>("/v1/auth/login", {
      email,
      password,
      org_slug: opts.orgSlug || undefined,
      // O endpoint de LOGIN espera `otp_code` (dto.LoginRequest). Enviar
      // `totp_code` (nome usado no aceite de convite) fazia o código ser
      // descartado → servidor via OTP vazio → TOTP_REQUIRED em loop.
      otp_code: opts.totp || undefined,
    });
  },

  async logout(): Promise<void> {
    await api.post("/v1/auth/logout", {}, { withCredentials: true });
  },

  async me(): Promise<MeBundle> {
    return getData<MeBundle>("/v1/me");
  },

  async listSessions(): Promise<SessionDTO[]> {
    return getData<SessionDTO[]>("/v1/me/sessions");
  },

  async revokeSession(id: string): Promise<void> {
    await api.delete<Envelope<void>>(`/v1/me/sessions/${id}`);
  },

  async startTOTP(): Promise<TOTPStart> {
    return postData<TOTPStart>("/v1/me/totp/start");
  },

  async confirmTOTP(code: string): Promise<void> {
    await api.post<Envelope<void>>("/v1/me/totp/confirm", { code });
  },

  async disableTOTP(): Promise<void> {
    await api.delete<Envelope<void>>("/v1/me/totp");
  },

  async requestPasswordReset(email: string, orgSlug?: string): Promise<void> {
    await api.post<Envelope<void>>("/v1/auth/password-reset/request", {
      email,
      org_slug: orgSlug || undefined,
    });
  },

  async confirmPasswordReset(token: string, newPassword: string): Promise<void> {
    await api.post<Envelope<void>>("/v1/auth/password-reset/confirm", {
      token,
      new_password: newPassword,
    });
  },

  // inviteInfo: projeção pública do convite (antes de aceitar). requires_totp
  // indica que o papel exige 2FA — então totp_secret/otpauth_url/qr_data_url vêm
  // preenchidos p/ o convidado ativar o TOTP na própria tela de aceite.
  async inviteInfo(token: string): Promise<InviteInfo> {
    return getData<InviteInfo>(`/v1/auth/invitations/${encodeURIComponent(token)}/info`);
  },

  async acceptInvitation(
    token: string,
    name: string,
    password: string,
    locale = "pt-BR",
    totp?: { secret: string; code: string },
  ): Promise<MeUser> {
    return postData<MeUser>(`/v1/auth/invitations/${encodeURIComponent(token)}/accept`, {
      name,
      password,
      locale,
      totp_secret: totp?.secret,
      totp_code: totp?.code,
    });
  },
};

export interface InviteInfo {
  email: string;
  name: string;
  requires_totp: boolean;
  totp_secret?: string;
  otpauth_url?: string;
  qr_data_url?: string;
}
